import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Button } from '@heroui/react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
        <div className="container mx-auto px-6 py-6 max-w-4xl">
          <Button
            as={RouterLink}
            to="/"
            variant="light"
            startContent={<ArrowLeftIcon className="w-4 h-4" />}
            className="mb-4"
          >
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-3">Privacy Policy</h1>
          <div className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
            <p><strong>Provider:</strong> Rimal Labs LLC</p>
            <p><strong>Site/App:</strong> ClipKite (ClipKite.com)</p>
            <p><strong>Contact (privacy):</strong> <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link></p>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="prose prose-gray max-w-none">

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">1. Scope</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Explains how we collect, use, and share information when you use ClipKite.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">2. Data We Collect</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li><strong className="text-gray-900 dark:text-slate-100">Account & profile:</strong> email, Firebase UID, email-verification status, username, display name, avatar key.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Content:</strong> uploads (videos/images), captions, comments, likes, reports; metadata (upload time, duration). We strip EXIF/GPS on processed images.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Usage & device:</strong> IP, browser/device, language, timestamps, referral, basic event logs.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Security/anti-abuse:</strong> auth identifiers, Cloudflare Turnstile signals, rate-limit counters, approximate region.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Moderation signals:</strong> automated checks for nudity/sexual content and violent/graphic content, with human review as needed.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Communications:</strong> messages you send us; notification preferences.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Analytics:</strong> none today. If we add measurement or similar services later, we'll update this policy and, where required, request consent.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">3. How We Use Data</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li>Provide features (login, profiles, uploads/streaming, comments/likes).</li>
              <li>Moderate and enforce rules (including automated analysis + human review).</li>
              <li>Transcode/deliver videos via Mux; store/serve images via Cloudflare R2/CDN.</li>
              <li>Security and abuse prevention (rate limits, bot checks).</li>
              <li>Transactional emails (verification, resets, critical notices).</li>
              <li>Improve reliability, troubleshoot, and (if enabled later) support content delivery or measurement services.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">4. Legal Bases (EEA/UK)</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li>Contract (providing the Service).</li>
              <li>Legitimate interests (security, moderation, improvement).</li>
              <li>Consent (where required for optional technologies).</li>
              <li>Legal obligation (lawful requests).</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">5. Sharing</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-3">
              We share data with service providers only to run the Service:
            </p>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300 mb-4">
              <li><strong className="text-gray-900 dark:text-slate-100">Authentication:</strong> Firebase.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Video processing/streaming:</strong> Mux.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Storage/CDN & bot protection:</strong> Cloudflare R2/CDN/Turnstile.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Database:</strong> MongoDB Atlas.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Email delivery:</strong> Resend.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Hosting/ops:</strong> Vercel (frontend), Google Cloud Run (API).</li>
            </ul>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-3">
              We may disclose information to comply with law, protect rights/safety, or as part of a merger/acquisition.
            </p>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              <strong className="text-gray-900 dark:text-slate-100">Future-readiness:</strong> If we later enable third-party technologies that provide content delivery, fraud prevention, personalization, or measurement, we will list those providers here, update choices/controls, and—where the law requires—request consent before using them.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">6. International Transfers</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We and our providers process data in the United States and other countries. Where required, we use safeguards (e.g., Standard Contractual Clauses).
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">7. Retention</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li><strong className="text-gray-900 dark:text-slate-100">Account & profile:</strong> while your account is active; deleted on request (subject to legal holds).</li>
              <li><strong className="text-gray-900 dark:text-slate-100">User content:</strong> until you delete it; residual backups for a short time.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Logs/security data:</strong> ~60 days.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">Backups:</strong> ~14 days.</li>
            </ul>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed mt-3">
              We may retain minimal records as required by law or to enforce our Terms.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">8. Your Choices & Rights</h2>
            <ul className="space-y-3 text-gray-700 dark:text-slate-300">
              <li>Access, correct, or delete your data in-app or via <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link>.</li>
              <li>Request export of your content/profile data.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">EEA/UK:</strong> additional rights to object/restrict; you can complain to your authority.</li>
              <li><strong className="text-gray-900 dark:text-slate-100">California (CPRA):</strong> we don't "sell" personal information. If our practices change such that the law treats certain activities as a "sale" or "sharing," we will provide a way to opt-out and update this policy before enabling them.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">9. Children</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Service is 16+. We don't knowingly collect data from children under 16.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">10. Cookies & Similar Tech</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed mb-3">
              We currently use strictly necessary cookies/tokens (auth and security, including Turnstile).
            </p>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              If we later add optional categories (e.g., measurement or personalization technologies), we'll expose controls and—where required—collect consent before use.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">11. Security</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We apply reasonable safeguards (TLS, access controls, encryption at rest by our providers, rate limits, bot challenges). No system is perfectly secure.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">12. Do Not Track</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We don't currently respond to DNT signals. Manage cookies in your browser.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">13. DMCA & IP Notices</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Send notices to <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link>. A designated DMCA agent and mailing address will be posted once appointed.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">14. Changes</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              We'll update this policy as needed and post the new date. Material changes get reasonable notice.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-50 mb-4 pb-2 border-b-2 border-primary-500">15. Contact</h2>
            <p className="text-gray-700 dark:text-slate-300 leading-relaxed">
              Rimal Labs LLC — <Link href="mailto:rimal.labs.dev@gmail.com" className="text-primary-600 hover:text-primary-700">rimal.labs.dev@gmail.com</Link>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
